// The shapes the habit rules read. They are the parts of the session player's SessionLog and of the
// data layer's records that the rules need, so both fit without conversion.
import type { CardioActivity } from '../cardio/activity';
import type { SectionFormat, SectionKind } from '../types';
import type { Day } from './dates';

export interface HabitBlock {
  sectionId: string;
  exerciseId: string;
  planned?: { reps: number | null; seconds: number | null; times: number | null };
  done: { reps: number; seconds: number };
  skipped: boolean;
  weightKg?: number | null;
  entries?: Array<{ reps: number; weightKg?: number; skipped: boolean; partial?: boolean }>;
}

export interface HabitSection {
  sectionId: string;
  kind: SectionKind;
  format: SectionFormat;
  plannedSec: number;
  doneSec: number;
  skipped: boolean;
}

/** A finished (or ended early) session. */
export interface HabitSession {
  id: string;
  workoutId: string;
  workoutName: string;
  startedAt: number;
  endedAt: number;
  activeSec: number;
  plannedSec?: number;
  completed: boolean;
  /** The main part was done to its end. Older records without it fall back to `completed`. */
  mainDone?: boolean;
  sections?: HabitSection[];
  blocks?: HabitBlock[];
  /**
   * Imported history (an activity older than 7 days at import): shown in the history and Progress, but no XP,
   * no streak, no weekly goal, badge or quest, and it uses no room in its week.
   */
  historyOnly?: boolean;
  /** A cardio activity (logged, imported or from the player): XP by minutes and effort when there is no workout. */
  activity?: CardioActivity;
}

/** One entry of the weekly plan (weekday 1 Monday to 7 Sunday, local time "HH:MM"). */
export interface HabitPlanEntry {
  id: string;
  weekday: number;
  time: string;
  workoutId: string | null;
}

export interface HabitXpEvent {
  id: string;
  day: Day;
  amount: number;
  reason: string;
  sessionId: string | null;
}

export interface HabitStreak {
  /** Training days in a row without a missed planned day. */
  currentDays: number;
  longestDays: number;
  /** The last day that counted. */
  lastDay: Day | null;
  /** Days a streak freeze covered. */
  freezeDays: Day[];
}

/** Whether a session counts as a session for streaks, the weekly goal, badges and bonuses. */
export function counts(s: Pick<HabitSession, 'mainDone' | 'completed' | 'historyOnly'>): boolean {
  if (s.historyOnly) return false;
  return s.mainDone ?? s.completed;
}
